import { memo, type ReactNode } from 'react'

import type {
  DataTableFilterValue,
  DataTablePagination,
  SortOrder,
} from '@/components/data-table/data-table.interface'
import { DataTable } from '@/components/data-table/data-table.organism'
import { cn } from '@/lib/utils'

import type { BillingUsageEntry } from './billing.interface'
import { usageColumnList } from './billing-token-usage-columns.helper'
import { getUsageRowId, usageTypeFilterList } from './billing-token-usage-table.helper'

export interface BillingTokenUsageTableProps {
  data: BillingUsageEntry[]
  pagination: DataTablePagination
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  searchValue?: string
  onSearchChange?: (value: string) => void
  sortBy?: string
  sortOrder?: SortOrder
  onSortChange?: (sortBy: string, sortOrder: SortOrder) => void
  filterValueList?: DataTableFilterValue[]
  onFilterChange?: (filterValueList: DataTableFilterValue[]) => void
  isLoading?: boolean
  className?: string
}

const BillingTokenUsageTableBase: React.FC<BillingTokenUsageTableProps> = ({
  data,
  pagination,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange,
  filterValueList,
  onFilterChange,
  isLoading = false,
  className,
}): ReactNode => (
  <DataTable
    data={data}
    columnList={usageColumnList}
    getRowId={getUsageRowId}
    pagination={pagination}
    onPageChange={onPageChange}
    onPageSizeChange={onPageSizeChange}
    searchValue={searchValue}
    onSearchChange={onSearchChange}
    searchPlaceholder="Search by step, resource, user, or chat..."
    sortBy={sortBy}
    sortOrder={sortOrder}
    onSortChange={onSortChange}
    filterList={onFilterChange ? usageTypeFilterList : undefined}
    filterValueList={filterValueList}
    onFilterChange={onFilterChange}
    isLoading={isLoading}
    emptyMessage="No usage records found."
    className={cn('billing-token-usage-table', className)}
  />
)

export const BillingTokenUsageTable = memo(BillingTokenUsageTableBase)

BillingTokenUsageTable.displayName = 'BillingTokenUsageTable'
